// ==========================================================================
// TerminPipelinePunkte — 7 Icon-Buttons mit Status (grau/rot/gruen)
// ==========================================================================
// Wird sowohl auf der Pipeline-Uebersicht als auch unter jedem Termin in
// der Akte verwendet. Jeder Punkt hat ein eigenes Icon:
//   1 = Calendar+Mail  (Termin-Mail an Kunde)
//   2 = CalendarCheck  (Termin stattgefunden, MANUELL)
//   3 = FileText       (Protokoll erstellt)
//   4 = MailCheck      (Begleit-Mail — DREI-STATUS: grau/rot/gruen)
//   5 = ClipboardList  (AS-Bremen-Warteliste)
//   6 = Send           (an AS Bremen versendet)
//   7 = Euro           (Abrechnung aus Berater-Sicht erledigt)
//
// Status-Werte pro Punkt:
//   1, 3, 5, 6, 7  : automatisch (gruen=erledigt / grau=offen)
//   2              : manuell (gruen / grau, klickbar = Toggle)
//   4              : drei-Status — grau (kein) / rot (geplant) / gruen (erledigt)
//
// Spalten 5-7 sind alle "AS Bremen"-bezogen; sie zeigen zusaetzlich oben
// rechts ein kleines "AS"-Badge, um den Bezug visuell klar zu machen.
// ==========================================================================

import React from 'react';
import { Calendar, CalendarCheck, FileText, MailCheck, ClipboardList, Send, Euro, Mail } from 'lucide-react';

const FARBE_GRUEN_BG = '#10b981';
const FARBE_GRUEN_HOV = '#059669';
const FARBE_ROT_BG = '#ef4444';
const FARBE_ROT_HOV = '#dc2626';
const FARBE_GRAU_BG = '#e5e7eb';
const FARBE_GRAU_FG = '#6b7280';

function formatDatum(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return null; }
}

function Punkt({ farbe, icon: Icon, label, iso, statusText, onClick, kompakt, asBadge }) {
  // farbe: 'gruen' | 'rot' | 'grau'
  const size = kompakt ? 28 : 32;
  const iconSize = kompakt ? 14 : 16;
  const isClick = typeof onClick === 'function';
  let bg, fg;
  if (farbe === 'gruen') { bg = FARBE_GRUEN_BG; fg = '#fff'; }
  else if (farbe === 'rot') { bg = FARBE_ROT_BG; fg = '#fff'; }
  else { bg = FARBE_GRAU_BG; fg = FARBE_GRAU_FG; }

  const datum = formatDatum(iso);
  const titel = `${label}${statusText ? ' — ' + statusText : ''}${datum ? ' (' + datum + ')' : ''}`;

  return (
    <button
      type="button"
      onClick={isClick ? onClick : undefined}
      disabled={!isClick}
      title={titel}
      aria-label={titel}
      style={{
        position: 'relative',
        width: size, height: size, borderRadius: 6,
        background: bg, color: fg,
        border: 'none',
        cursor: isClick ? 'pointer' : 'default',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: 0,
        transition: 'background 0.15s, transform 0.1s',
      }}
      onMouseDown={e => { if (isClick) e.currentTarget.style.transform = 'scale(0.92)'; }}
      onMouseUp={e => { if (isClick) e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { if (isClick) e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <Icon size={iconSize} strokeWidth={2.25} />
      {asBadge && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          background: '#111827', color: '#fff',
          fontSize: 8, fontWeight: 700, lineHeight: 1,
          padding: '2px 3px', borderRadius: 3,
          letterSpacing: 0.2,
        }}>AS</span>
      )}
    </button>
  );
}

/**
 * @param {object} props
 * @param {object} props.pipeline   Status-Objekt vom Backend (termin.pipeline).
 * @param {function=} props.onToggleStattgefunden  Klick auf Punkt 2 (Toggle).
 * @param {function=} props.onToggleBegleitMail    Klick auf Punkt 4 (3-Status-Cycle).
 * @param {boolean=} props.kompakt
 */
export default function TerminPipelinePunkte({
  pipeline,
  onToggleStattgefunden,
  onToggleBegleitMail,
  onToggleAbrechnungErledigt,
  kompakt = false,
}) {
  const p = pipeline || {};
  const s1 = !!p.spalte_1_done;
  const s2 = !!p.spalte_2_done;
  const s3 = !!p.spalte_3_done;
  const begleit = p.begleit_mail_status || 'kein';
  const s4Farbe = begleit === 'erledigt' ? 'gruen' : begleit === 'geplant' ? 'rot' : 'grau';
  const s5 = !!p.spalte_5_done;
  const s6 = !!p.spalte_6_done;
  const s7 = !!p.spalte_7_done;

  const gap = kompakt ? 6 : 8;
  return (
    <div style={{ display: 'inline-flex', gap, alignItems: 'center' }}>
      <Punkt
        farbe={s1 ? 'gruen' : 'grau'}
        icon={Mail}
        label="Termin-Mail an Kunde"
        iso={p.info_mail_gesendet_am}
        statusText={s1 ? 'verschickt' : 'noch offen'}
        kompakt={kompakt}
      />
      <Punkt
        farbe={s2 ? 'gruen' : 'grau'}
        icon={CalendarCheck}
        label="Termin stattgefunden"
        iso={p.stattgefunden_am}
        statusText={s2 ? 'erledigt' : 'klick zum Abhaken'}
        onClick={onToggleStattgefunden}
        kompakt={kompakt}
      />
      <Punkt
        farbe={s3 ? 'gruen' : 'grau'}
        icon={FileText}
        label="Protokoll erstellt"
        statusText={s3 ? 'PDF vorhanden' : 'noch nicht erstellt'}
        kompakt={kompakt}
      />
      <Punkt
        farbe={s4Farbe}
        icon={MailCheck}
        label="Begleit-Mail"
        iso={p.protokoll_mail_versendet_am}
        statusText={
          begleit === 'erledigt' ? 'erledigt' :
          begleit === 'geplant'  ? 'noch zu schreiben (klick = abhaken)' :
                                   'klick = "plane Begleit-Mail"'
        }
        onClick={onToggleBegleitMail}
        kompakt={kompakt}
      />
      <Punkt
        farbe={s5 ? 'gruen' : 'grau'}
        icon={ClipboardList}
        label="AS-Bremen-Warteliste"
        iso={p.abrechnung_markiert_am}
        statusText={s5 ? 'in Warteliste' : 'noch nicht markiert'}
        asBadge
        kompakt={kompakt}
      />
      <Punkt
        farbe={s6 ? 'gruen' : 'grau'}
        icon={Send}
        label="An AS Bremen versendet"
        iso={p.abrechnung_versendet_am}
        statusText={s6 ? 'versendet' : 'noch nicht raus'}
        asBadge
        kompakt={kompakt}
      />
      <Punkt
        farbe={s7 ? 'gruen' : 'grau'}
        icon={Euro}
        label="Abrechnung eingegangen (manuell pruefen + abhaken)"
        iso={p.abrechnung_erledigt_am}
        statusText={s7 ? 'eingegangen' : 'noch nicht geprueft'}
        onClick={onToggleAbrechnungErledigt}
        asBadge
        kompakt={kompakt}
      />
    </div>
  );
}

// Export der Icon-Map fuer den Tabellen-Spaltenkopf — Pipeline-Tabelle nutzt
// dieselben Icons, damit Kopf und Punkt eindeutig zusammenpassen.
export const SPALTEN_ICONS = [
  { icon: Mail,           label: 'Termin-Mail an Kunde' },
  { icon: CalendarCheck,  label: 'Termin stattgefunden' },
  { icon: FileText,       label: 'Protokoll erstellt' },
  { icon: MailCheck,      label: 'Begleit-Mail (rot = noch zu schreiben)' },
  { icon: ClipboardList,  label: 'AS-Bremen-Warteliste', asBadge: true },
  { icon: Send,           label: 'An AS Bremen versendet', asBadge: true },
  { icon: Euro,           label: 'Abrechnung erledigt',    asBadge: true },
];
